import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../config/api';
import { FaTimes, FaArrowLeft, FaPlus, FaFilePdf } from 'react-icons/fa';
import stadiumPlan from '../../assets/images/mappe_allianz_stadium.png';
import { getMergedSectorStringsForHome, isMergedGroupInList, getZoneFromSector } from '../../utils/stadiumSectorGroups';
import { supportsSubscriberNotify } from '../../utils/subscriberTicketRules';
import { useTicketCompetitionTeams } from '../../hooks/useTicketCompetitionTeams';
import {
  parseDateTimeInTimeZone,
  splitDateTimeForInput,
} from '../../utils/matchDateTime';
import { getTeamLogo, hasTeamLogo } from '../../utils/teamLogos';
import { loadCustomTeamLogos, uploadTeamLogo } from '../../utils/customTeamLogos';
import './AdminPages.css';
import '../TicketPayment.css';

const MAX_SALES_PHASES = 7;

const AdminTicketForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  
  const [loading, setLoading] = useState(isEditing);
  const [submitting, setSubmitting] = useState(false);
  const [tempSelectedSectors, setTempSelectedSectors] = useState([]); // Temporarily selected sectors (not yet priced)
  const [pricingGroups, setPricingGroups] = useState([]); // Array of { sectors: string[], price: number, hasMinorPrice?: boolean, minorPrice?: number, id: string }
  const [currentPrice, setCurrentPrice] = useState('');
  const [currentHasMinorPrice, setCurrentHasMinorPrice] = useState(false);
  const [currentMinorPrice, setCurrentMinorPrice] = useState('');
  const [hoveredSector, setHoveredSector] = useState(null);
  const [hoveredAreaCoords, setHoveredAreaCoords] = useState(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 775, height: 615 });
  const [displayDimensions, setDisplayDimensions] = useState({ width: 775, height: 615 });
  const stadiumImageRef = useRef(null);
  const pdfInputRef = useRef(null);
  const opponentLogoInputRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [pdfImporting, setPdfImporting] = useState(false);
  const [pdfImportFeedback, setPdfImportFeedback] = useState(null);
  const [subscriberEmailsSent, setSubscriberEmailsSent] = useState(false);
  const [teamLogosTick, setTeamLogosTick] = useState(0);
  const [opponentLogoUploading, setOpponentLogoUploading] = useState(false);

  const { competitions } = useTicketCompetitionTeams();

  const stadiums = [
    'Allianz Stadium',
    'Stadio Giuseppe Meazza (San Siro)',
    'Stadio Olimpico',
    'Stadio Diego Armando Maradona',
    'Gewiss Stadium',
    'Stadio Artemio Franchi',
    'Stadio Renato Dall\'Ara',
    'Stadio Olimpico Grande Torino',
    'Bluenergy Stadium (Stadio Friuli)',
    'Stadio Marcantonio Bentegodi',
    'Stadio Luigi Ferraris',
    'Mapei Stadium – Città del Tricolore',
    'Stadio Via del Mare',
    'Stadio Ennio Tardini',
    'Unipol Domus',
    'Stadio Giovanni Zini',
    'Stadio Giuseppe Sinigaglia',
    'Arena Garibaldi – Stadio Romeo Anconetani',
    'Stadio San Nicola',
    'Stadio Renzo Barbera',
    'Stadio Mario Rigamonti',
    'Stadio Nicola Ceravolo',
    'Orogel Stadium – Dino Manuzzi',
    'Stadio San Vito – Gigi Marulla',
    'Stadio Benito Stirpe',
    'Stadio Pier Cesare Tombolato',
    'Stadio Alberto Picco',
    'Stadio Druso',
    'Stadio Carlo Castellani',
    'Stadio Arechi',
    'Stadio Romeo Menti',
    'Stadio Braglia',
    'Stadio Del Duca',
    'Stadio Adriatico – Giovanni Cornacchia',
    'Stadio Paolo Mazza',
    'Stadio Nereo Rocco'
  ];

  const CUSTOM_STADIUM_VALUE = '__other__';
  const CUSTOM_OPPONENT_VALUE = '__custom_opponent__';

  const [formData, setFormData] = useState({
    competition: '',
    isHome: true,
    opponentTeam: '',
    matchTitle: '',
    matchDate: '',
    matchTime: '',
    stadium: 'Allianz Stadium',
    status: 'available',
    description: '',
    imageUrl: '',
    active: true,
    salesPhases: [],
    price: '', // For away matches
    hasMinorPrice: false,
    minorPrice: '',
    responseDeadlineDate: '',
    responseDeadlineTime: '23:59',
    competitionPhase: '',
    europeanHomeMatchIndex: '',
    serieAHomeMatchIndex: '',
    coppaMatchNumber: '',
  });

  const [customStadium, setCustomStadium] = useState('');
  const [customOpponentTeam, setCustomOpponentTeam] = useState('');
  const [useCustomOpponent, setUseCustomOpponent] = useState(false);

  const competitionTeams = formData.competition
    ? (competitions[formData.competition]?.teams ?? [])
    : [];

  const opponentSelectValue = useCustomOpponent
    ? CUSTOM_OPPONENT_VALUE
    : formData.opponentTeam;

  const showCustomOpponentInput = useCustomOpponent;

  const stadiumSelectValue = stadiums.includes(formData.stadium)
    ? formData.stadium
    : CUSTOM_STADIUM_VALUE;

  const showCustomStadiumInput = stadiumSelectValue === CUSTOM_STADIUM_VALUE;

  const isHomeSerieA = formData.isHome && formData.competition === 'Serie A';
  const supportsSubscriberEmails = supportsSubscriberNotify(formData);
  const isEuropeanCompetition = [
    'Champions League',
    'UEFA Europa League',
    'UEFA Europa Conference League',
  ].includes(formData.competition);
  const isEuropeanHome = isEuropeanCompetition && formData.isHome;
  const isCoppaItalia = formData.competition === 'Coppa Italia';
  const isCoppaHome = isCoppaItalia && formData.isHome;

  const resolvedOpponentName = showCustomOpponentInput
    ? customOpponentTeam.trim()
    : (formData.opponentTeam || '').trim();

  const opponentHasLogo = resolvedOpponentName
    ? hasTeamLogo(resolvedOpponentName)
    : false;
  const opponentLogoSrc = resolvedOpponentName ? getTeamLogo(resolvedOpponentName) : null;
  const juventusLogoSrc = getTeamLogo('Juventus');

  useEffect(() => {
    loadCustomTeamLogos().then(() => setTeamLogosTick((t) => t + 1));
  }, []);

  const handleOpponentLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !resolvedOpponentName) return;

    setOpponentLogoUploading(true);
    try {
      await uploadTeamLogo(resolvedOpponentName, file);
      setTeamLogosTick((t) => t + 1);
    } catch (error) {
      console.error('Opponent logo upload error:', error);
      const msg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Erreur lors de l\'envoi du logo';
      alert(msg);
    } finally {
      setOpponentLogoUploading(false);
    }
  };

  const handleStadiumSelectChange = (e) => {
    const value = e.target.value;
    if (value === CUSTOM_STADIUM_VALUE) {
      setFormData({
        ...formData,
        stadium: customStadium.trim(),
      });
    } else {
      setCustomStadium('');
      setFormData({
        ...formData,
        stadium: value,
      });
    }
  };

  const handleCustomStadiumChange = (e) => {
    const value = e.target.value;
    setCustomStadium(value);
    setFormData({
      ...formData,
      stadium: value,
    });
  };

  const handleOpponentSelectChange = (e) => {
    const value = e.target.value;
    if (value === CUSTOM_OPPONENT_VALUE) {
      setUseCustomOpponent(true);
      setFormData({
        ...formData,
        opponentTeam: customOpponentTeam.trim(),
      });
    } else {
      setUseCustomOpponent(false);
      setCustomOpponentTeam('');
      setFormData({
        ...formData,
        opponentTeam: value,
      });
    }
  };

  const handleCustomOpponentChange = (e) => {
    const value = e.target.value;
    setCustomOpponentTeam(value);
    setFormData({
      ...formData,
      opponentTeam: value,
    });
  };

  useEffect(() => {
    if (isEditing) {
      fetchTicket();
    }
  }, [id, isEditing]);

  const fetchTicket = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/tickets/${id}`);
      const ticket = response.data;
      
      const { date: matchDate, time: matchTime } = splitDateTimeForInput(ticket.date);
      
      // Parse sales phases
      const salesPhases = (ticket.salesPhases || []).map(phase => {
        const start = splitDateTimeForInput(phase.startDate);
        const end = splitDateTimeForInput(phase.endDate);
        return {
          title: phase.title,
          titleIt: phase.titleIt || '',
          startDate: start.date,
          startTime: start.time,
          endDate: end.date,
          endTime: end.time
        };
      });

      // Parse area pricing into pricingGroups
      const groups = [];
      if (ticket.areaPricing) {
        ticket.areaPricing.forEach(pricing => {
          if (pricing.hasSectors && pricing.sectors) {
            // Group sectors by price and minor price
            const priceMap = {};
            pricing.sectors.forEach(sector => {
              const price = sector.price || 0;
              const hasMinorPrice = sector.minorPrice != null && sector.minorPrice > 0;
              const minorPrice = hasMinorPrice ? sector.minorPrice : null;
              const key = `${price}::${hasMinorPrice ? minorPrice : 'none'}`;
              if (!priceMap[key]) {
                priceMap[key] = { price, hasMinorPrice, minorPrice, sectors: [] };
              }
              priceMap[key].sectors.push(sector.name);
            });
            // Create pricing groups
            Object.values(priceMap).forEach((entry) => {
              groups.push({
                id: `group-${Date.now()}-${Math.random()}`,
                sectors: entry.sectors,
                price: parseFloat(entry.price),
                hasMinorPrice: entry.hasMinorPrice,
                minorPrice: entry.hasMinorPrice ? parseFloat(entry.minorPrice) : undefined,
              });
            });
          }
        });
      }
      setPricingGroups(groups);

      // Extract opponent and home/away
      let isHome = true;
      let opponentTeam = '';
      let competition = ticket.competition || '';
      
      if (ticket.opponentTeam) {
        opponentTeam = ticket.opponentTeam;
        isHome = ticket.isHome !== undefined ? ticket.isHome : true;
      } else if (ticket.matchTitle) {
        if (ticket.matchTitle.includes(' vs ')) {
          const parts = ticket.matchTitle.split(' vs ');
          if (parts[0].trim() === 'Juventus') {
            isHome = true;
            opponentTeam = parts[1].trim();
          } else {
            isHome = false;
            opponentTeam = parts[0].trim();
          }
        }
      }

      setSubscriberEmailsSent(Boolean(ticket.subscriberNotification?.sent));
      setFormData({
        competition: competition,
        isHome: isHome,
        opponentTeam: opponentTeam,
        matchTitle: ticket.matchTitle,
        matchDate: matchDate,
        matchTime: matchTime,
        stadium: ticket.stadium,
        status: ticket.status || 'available',
        description: ticket.description || '',
        imageUrl: ticket.imageUrl || '',
        active: ticket.active,
        salesPhases: salesPhases,
        price: ticket.price || '',
        hasMinorPrice: Boolean(ticket.hasMinorPrice && ticket.minorPrice),
        minorPrice: ticket.minorPrice || '',
        responseDeadlineDate: ticket.subscriberResponseDeadline
          ? splitDateTimeForInput(ticket.subscriberResponseDeadline).date
          : '',
        responseDeadlineTime: ticket.subscriberResponseDeadline
          ? splitDateTimeForInput(ticket.subscriberResponseDeadline).time
          : '23:59',
        competitionPhase: ticket.competitionPhase || '',
        europeanHomeMatchIndex: ticket.europeanHomeMatchIndex
          ? String(ticket.europeanHomeMatchIndex)
          : ticket.competitionPhase === 'after_group_stage'
            ? '5'
            : ticket.competitionPhase === 'group_stage'
              ? '1'
              : '',
        serieAHomeMatchIndex: ticket.serieAHomeMatchIndex ? String(ticket.serieAHomeMatchIndex) : '',
        coppaMatchNumber: ticket.coppaMatchNumber ? String(ticket.coppaMatchNumber) : '',
      });
      setCustomStadium(
        ticket.stadium && !stadiums.includes(ticket.stadium) ? ticket.stadium : ''
      );
      const teams = competitions[competition]?.teams ?? [];
      const isCustomOpponent = Boolean(
        opponentTeam && (teams.length === 0 || !teams.includes(opponentTeam)),
      );
      setUseCustomOpponent(isCustomOpponent);
      setCustomOpponentTeam(isCustomOpponent ? opponentTeam : '');
    } catch (error) {
      console.error('Error fetching ticket:', error);
      alert('Erreur lors du chargement du billet');
      navigate('/admin/tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!formData.competition || !formData.opponentTeam) return;
    const teams = competitions[formData.competition]?.teams ?? [];
    if (teams.length > 0 && !teams.includes(formData.opponentTeam)) {
      setUseCustomOpponent(true);
      setCustomOpponentTeam(formData.opponentTeam);
    }
  }, [competitions, formData.competition, formData.opponentTeam]);

  // Update display dimensions when image loads or window resizes
  useEffect(() => {
    if (!imageLoaded || !stadiumImageRef.current) return;

    const updateDimensions = () => {
      const img = stadiumImageRef.current;
      if (img) {
        void img.offsetWidth;
        // Use clientWidth/clientHeight for actual displayed size
        const displayWidth = img.clientWidth || img.offsetWidth || img.naturalWidth || 775;
        const displayHeight = img.clientHeight || img.offsetHeight || img.naturalHeight || 615;
        setDisplayDimensions({
          width: displayWidth,
          height: displayHeight
        });
        
        // Update map coordinates after dimensions are updated
        setTimeout(() => {
          const mapElement = document.getElementById('stadium-map-admin');
          if (mapElement && img) {
            const originalWidth = imageDimensions.width;
            const originalHeight = imageDimensions.height;
            // Use clientWidth/clientHeight for actual displayed size
            const actualWidth = img.clientWidth || img.offsetWidth || img.naturalWidth || originalWidth;
            const actualHeight = img.clientHeight || img.offsetHeight || img.naturalHeight || originalHeight;
            const scaleX = actualWidth / originalWidth;
            const scaleY = actualHeight / originalHeight;

            const areas = mapElement.querySelectorAll('area');
            areas.forEach(area => {
              let originalCoords = area.getAttribute('data-original-coords');
              if (!originalCoords) {
                originalCoords = area.getAttribute('coords');
                area.setAttribute('data-original-coords', originalCoords);
              }

              const shape = area.getAttribute('shape');
              const coordsArray = originalCoords.split(',').map(c => parseFloat(c.trim()));
              
              const scaledCoords = coordsArray.map((coord, index) => {
                if (shape === 'rect') {
                  return index % 2 === 0 ? Math.round(coord * scaleX) : Math.round(coord * scaleY);
                } else if (shape === 'poly') {
                  return index % 2 === 0 ? Math.round(coord * scaleX) : Math.round(coord * scaleY);
                } else if (shape === 'circle') {
                  if (index === 2) {
                    return Math.round(coord * ((scaleX + scaleY) / 2));
                  }
                  return index === 0 ? Math.round(coord * scaleX) : Math.round(coord * scaleY);
                }
                return coord;
              });

              area.setAttribute('coords', scaledCoords.join(','));
            });
          }
        }, 200);
      }
    };

    const timeoutId = setTimeout(updateDimensions, 100);
    window.addEventListener('resize', updateDimensions);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateDimensions);
    };
  }, [imageLoaded, isMobile, imageDimensions]);

  // Helper function to parse coords and return shape info
  const parseAreaCoords = useCallback((coords, shape) => {
    if (!coords) return null;
    const coordsArray = coords.split(',').map(c => parseFloat(c.trim()));
    
    if (shape === 'rect') {
      const x1 = coordsArray[0];
      const y1 = coordsArray[1];
      const x2 = coordsArray[2];
      const y2 = coordsArray[3];
      return {
        type: 'rect',
        x: Math.min(x1, x2),
        y: Math.min(y1, y2),
        width: Math.abs(x2 - x1),
        height: Math.abs(y2 - y1)
      };
    } else if (shape === 'poly') {
      const points = [];
      for (let i = 0; i < coordsArray.length; i += 2) {
        if (i + 1 < coordsArray.length) {
          points.push({ x: coordsArray[i], y: coordsArray[i + 1] });
        }
      }
      return {
        type: 'poly',
        points: points
      };
    }
    
    return null;
  }, []);

  // Function to update map coordinates based on image scale
  const updateMapCoordinates = useCallback(() => {
    if (!stadiumImageRef.current || !imageLoaded) return;
    
    const img = stadiumImageRef.current;
    const mapElement = document.getElementById('stadium-map-admin');
    if (!mapElement) return;

    // Get actual displayed dimensions - use getBoundingClientRect for most accurate size
    const rect = img.getBoundingClientRect();
    const displayedWidth = rect.width || img.clientWidth || img.offsetWidth || img.naturalWidth || 775;
    const displayedHeight = rect.height || img.clientHeight || img.offsetHeight || img.naturalHeight || 615;
    
    const originalWidth = imageDimensions.width; // 775
    const originalHeight = imageDimensions.height; // 615

    // Calculate scale factors
    const scaleX = displayedWidth / originalWidth;
    const scaleY = displayedHeight / originalHeight;

    const areas = mapElement.querySelectorAll('area');
    areas.forEach(area => {
      let originalCoords = area.getAttribute('data-original-coords');
      
      // Store original coords if not already stored
      if (!originalCoords) {
        originalCoords = area.getAttribute('coords');
        if (originalCoords) {
          area.setAttribute('data-original-coords', originalCoords);
        } else {
          return; // Skip if no coords
        }
      }

      const shape = area.getAttribute('shape');
      const coordsArray = originalCoords.split(',').map(c => parseFloat(c.trim())).filter(c => !isNaN(c));
      
      if (coordsArray.length === 0) return;
      
      // Scale coordinates based on shape
      const scaledCoords = coordsArray.map((coord, index) => {
        if (shape === 'rect') {
          // For rect: x1,y1,x2,y2 - scale x and y separately
          return index % 2 === 0 ? Math.round(coord * scaleX) : Math.round(coord * scaleY);
        } else if (shape === 'poly') {
          // For poly: x1,y1,x2,y2,x3,y3,... - scale x and y separately
          return index % 2 === 0 ? Math.round(coord * scaleX) : Math.round(coord * scaleY);
        } else if (shape === 'circle') {
          // For circle: x,y,radius - scale x, y, and radius
          if (index === 2) {
            // radius - use average scale
            return Math.round(coord * ((scaleX + scaleY) / 2));
          }
          return index === 0 ? Math.round(coord * scaleX) : Math.round(coord * scaleY);
        }
        return coord;
      });

      // Update the coords attribute
      area.setAttribute('coords', scaledCoords.join(','));
    });
  }, [imageLoaded, imageDimensions]);

  // Update map coordinates when image size changes
  useEffect(() => {
    if (!imageLoaded) return;
    
    // Multiple attempts to ensure coordinates are updated after image is fully rendered
    const updateWithDelay = (delay) => {
      setTimeout(() => {
        updateMapCoordinates();
      }, delay);
    };
    
    updateWithDelay(50);
    updateWithDelay(200);
    updateWithDelay(500);
    
    // Also update on window resize
    const handleResize = () => {
      setTimeout(() => updateMapCoordinates(), 100);
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [imageLoaded, displayDimensions, isMobile, updateMapCoordinates]);

  // Set up image map area click and hover handlers
  useEffect(() => {
    if (!imageLoaded) return;

    const mapElement = document.getElementById('stadium-map-admin');
    if (!mapElement) return;

    const areas = mapElement.querySelectorAll('area');
    
    const clickHandlers = new Map();
    const touchHandlers = new Map();
    const mouseEnterHandlers = new Map();
    const mouseLeaveHandlers = new Map();
    
    areas.forEach(area => {
      const sectorNumStr = area.getAttribute('title');
      const isLegendsClub = sectorNumStr && sectorNumStr.toLowerCase() === 'legends club';
      const sectorNum = isLegendsClub ? 'Legends club' : parseInt(sectorNumStr);
      
      if (!isLegendsClub && (isNaN(sectorNum) || sectorNum <= 0)) {
        return; // Skip invalid sectors
      }

      const handleAreaClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const sector = isLegendsClub ? 'Legends club' : sectorNumStr;
        const allPricedSectors = pricingGroups.flatMap(g => g.sectors);
        const isInGroup = isLegendsClub
          ? allPricedSectors.includes(sector)
          : isMergedGroupInList(sectorNum, allPricedSectors);
        if (!isInGroup) {
          toggleTempSectorSelection(sector, sectorNum);
        }
      };

      // Touch handler for mobile devices
      const handleAreaTouch = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Ensure coordinates are up to date before handling touch
        updateMapCoordinates();
        
        const sector = isLegendsClub ? 'Legends club' : sectorNumStr;
        const allPricedSectors = pricingGroups.flatMap(g => g.sectors);
        const isInGroup = isLegendsClub
          ? allPricedSectors.includes(sector)
          : isMergedGroupInList(sectorNum, allPricedSectors);
        if (!isInGroup) {
          toggleTempSectorSelection(sector, sectorNum);
        }
      };

      const handleAreaMouseEnter = (e) => {
        const coords = area.getAttribute('coords');
        const shape = area.getAttribute('shape');
        const areaInfo = parseAreaCoords(coords, shape);
        if (areaInfo) {
          setHoveredAreaCoords(areaInfo);
          setHoveredSector(isLegendsClub ? 'Legends club' : sectorNumStr);
        }
      };

      const handleAreaMouseLeave = () => {
        setHoveredAreaCoords(null);
        setHoveredSector(null);
      };
      
      clickHandlers.set(area, handleAreaClick);
      touchHandlers.set(area, handleAreaTouch);
      mouseEnterHandlers.set(area, handleAreaMouseEnter);
      mouseLeaveHandlers.set(area, handleAreaMouseLeave);
      
      // Add click handler for desktop
      area.addEventListener('click', handleAreaClick);
      // Add touch handlers for mobile devices
      area.addEventListener('touchstart', handleAreaTouch, { passive: false });
      area.addEventListener('touchend', handleAreaTouch, { passive: false });
      // Add mouse handlers for desktop hover effects
      area.addEventListener('mouseenter', handleAreaMouseEnter);
      area.addEventListener('mouseleave', handleAreaMouseLeave);
      
      area.setAttribute('href', 'javascript:void(0)');
    });

    return () => {
      clickHandlers.forEach((handler, area) => {
        area.removeEventListener('click', handler);
      });
      touchHandlers.forEach((handler, area) => {
        area.removeEventListener('touchstart', handler);
        area.removeEventListener('touchend', handler);
      });
      mouseEnterHandlers.forEach((handler, area) => {
        area.removeEventListener('mouseenter', handler);
      });
      mouseLeaveHandlers.forEach((handler, area) => {
        area.removeEventListener('mouseleave', handler);
      });
      clickHandlers.clear();
      touchHandlers.clear();
      mouseEnterHandlers.clear();
      mouseLeaveHandlers.clear();
    };
  }, [imageLoaded, parseAreaCoords, pricingGroups, updateMapCoordinates, formData.isHome]);

  // For home matches: sectors 106-109, 206-210, 118-122, 222-228 are merged (select one = select all)
  const toggleTempSectorSelection = (sector, sectorNum) => {
    const merged = formData.isHome && sectorNum != null ? getMergedSectorStringsForHome(sectorNum) : null;
    setTempSelectedSectors(prev => {
      const toToggle = merged || [sector];
      const anySelected = toToggle.some(s => prev.includes(s));
      if (anySelected) {
        return prev.filter(s => !toToggle.includes(s));
      } else {
        return [...prev.filter(s => !toToggle.includes(s)), ...toToggle];
      }
    });
  };

  const savePricingGroup = () => {
    if (tempSelectedSectors.length === 0) {
      alert('Veuillez sélectionner au moins un secteur');
      return;
    }
    if (!currentPrice || parseFloat(currentPrice) <= 0) {
      alert('Veuillez entrer un prix valide');
      return;
    }
    if (currentHasMinorPrice && (!currentMinorPrice || parseFloat(currentMinorPrice) <= 0)) {
      alert('Veuillez entrer un prix mineur valide');
      return;
    }

    const newGroup = {
      id: `group-${Date.now()}-${Math.random()}`,
      sectors: [...tempSelectedSectors],
      price: parseFloat(currentPrice),
      hasMinorPrice: currentHasMinorPrice,
      minorPrice: currentHasMinorPrice ? parseFloat(currentMinorPrice) : undefined,
    };

    setPricingGroups(prev => [...prev, newGroup]);
    setTempSelectedSectors([]);
    setCurrentPrice('');
    setCurrentHasMinorPrice(false);
    setCurrentMinorPrice('');
  };

  const removePricingGroup = (groupId) => {
    setPricingGroups(prev => prev.filter(g => g.id !== groupId));
  };

  const updatePricingGroupMinorPrice = (groupId, hasMinorPrice, minorPrice) => {
    setPricingGroups((prev) =>
      prev.map((group) => {
        if (group.id !== groupId) return group;
        return {
          ...group,
          hasMinorPrice,
          minorPrice:
            hasMinorPrice && minorPrice !== '' && !Number.isNaN(parseFloat(minorPrice))
              ? parseFloat(minorPrice)
              : undefined,
        };
      }),
    );
  };

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    if (name === 'competition') {
      setUseCustomOpponent(false);
      setCustomOpponentTeam('');
      setFormData({
        ...formData,
        competition: fieldValue,
        opponentTeam: '',
      });
      return;
    }

    setFormData({
      ...formData,
      [name]: fieldValue,
    });
  };

  const handleSalesPhaseChange = (index, field, value) => {
    const updatedPhases = [...formData.salesPhases];
    updatedPhases[index] = {
      ...updatedPhases[index],
      [field]: value
    };
    setFormData({
      ...formData,
      salesPhases: updatedPhases
    });
  };

  const addSalesPhase = () => {
    if (formData.salesPhases.length >= MAX_SALES_PHASES) {
      alert(`Vous ne pouvez ajouter que ${MAX_SALES_PHASES} phases de vente maximum`);
      return;
    }
    setFormData({
      ...formData,
      salesPhases: [
        ...formData.salesPhases,
        {
          title: '',
          titleIt: '',
          startDate: '',
          startTime: '',
          endDate: '',
          endTime: ''
        }
      ]
    });
  };

  const removeSalesPhase = (index) => {
    const updatedPhases = formData.salesPhases.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      salesPhases: updatedPhases
    });
  };

  const applyParsedTicketPdf = (parsed) => {
    const resolvedCompetition = parsed.competition || formData.competition;
    const teams = resolvedCompetition
      ? (competitions[resolvedCompetition]?.teams ?? [])
      : [];
    const opponentName = parsed.opponentTeam || '';
    const useCustom =
      Boolean(opponentName) &&
      (parsed.opponentCustom || (teams.length > 0 && !teams.includes(opponentName)));

    setFormData((prev) => ({
      ...prev,
      ...(parsed.competition ? { competition: parsed.competition } : {}),
      isHome: parsed.isHome !== undefined ? parsed.isHome : prev.isHome,
      opponentTeam: opponentName || prev.opponentTeam,
      matchDate: parsed.matchDate || prev.matchDate,
      matchTime: parsed.matchTime || prev.matchTime,
      stadium: parsed.stadium || prev.stadium,
      salesPhases:
        Array.isArray(parsed.salesPhases) && parsed.salesPhases.length > 0
          ? parsed.salesPhases
          : prev.salesPhases,
    }));

    if (opponentName) {
      setUseCustomOpponent(useCustom);
      setCustomOpponentTeam(useCustom ? opponentName : '');
    }

    if (parsed.stadium) {
      const knownStadium = stadiums.includes(parsed.stadium);
      setCustomStadium(knownStadium ? '' : parsed.stadium);
    }

    if (parsed.isHome && Array.isArray(parsed.pricingGroups) && parsed.pricingGroups.length > 0) {
      setPricingGroups(parsed.pricingGroups);
      setTempSelectedSectors([]);
      setCurrentPrice('');
      setCurrentHasMinorPrice(false);
      setCurrentMinorPrice('');
    }

    const filled = [
      parsed.competition && 'compétition',
      opponentName && 'équipe adverse',
      parsed.matchDate && 'date',
      parsed.matchTime && 'heure',
      parsed.salesPhases?.length && 'phases de vente',
      parsed.pricingGroups?.length && 'prix par secteur',
    ].filter(Boolean);

    const warningText =
      Array.isArray(parsed.warnings) && parsed.warnings.length > 0
        ? `\n\nÀ vérifier :\n${parsed.warnings.map((w) => `• ${w}`).join('\n')}`
        : '';

    setPdfImportFeedback({
      type: filled.length > 0 ? 'success' : 'warning',
      message:
        filled.length > 0
          ? `Formulaire prérempli : ${filled.join(', ')}.${warningText}`
          : `Peu d'informations détectées dans le PDF.${warningText}`,
    });
  };

  const handlePdfImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      alert('Veuillez sélectionner un fichier PDF.');
      return;
    }

    setPdfImporting(true);
    setPdfImportFeedback(null);
    try {
      const body = new FormData();
      body.append('pdf', file);
      const { data } = await api.post('/tickets/parse-pdf', body);
      applyParsedTicketPdf(data);
    } catch (error) {
      console.error('PDF import error:', error);
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Erreur inconnue';
      setPdfImportFeedback({ type: 'error', message: `Échec de l'import PDF : ${msg}` });
    } finally {
      setPdfImporting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const stadiumName = showCustomStadiumInput
      ? customStadium.trim()
      : formData.stadium.trim();

    if (!stadiumName) {
      alert('Veuillez saisir le nom du stade');
      return;
    }

    const opponentName = showCustomOpponentInput
      ? customOpponentTeam.trim()
      : formData.opponentTeam.trim();

    if (!opponentName) {
      alert('Veuillez sélectionner ou saisir l\'équipe adverse');
      return;
    }
    
    // For home matches, require pricing groups
    if (formData.isHome && pricingGroups.length === 0) {
      alert('Veuillez définir au moins un groupe de secteurs avec un prix');
      return;
    }

    if (formData.isHome) {
      const invalidMinorGroup = pricingGroups.find(
        (group) =>
          group.hasMinorPrice &&
          (group.minorPrice == null || Number.isNaN(group.minorPrice) || group.minorPrice <= 0),
      );
      if (invalidMinorGroup) {
        alert('Veuillez entrer un prix mineur valide pour chaque groupe concerné');
        return;
      }

      const unmappedSectors = [
        ...new Set(
          pricingGroups.flatMap((group) =>
            group.sectors.filter((sector) => !getZoneFromSector(sector)),
          ),
        ),
      ];
      if (unmappedSectors.length > 0) {
        alert(
          `Secteur(s) non reconnus sur le plan du stade : ${unmappedSectors.join(', ')}. Contactez le support technique.`,
        );
        return;
      }
    }
    
    // For away matches, require a single price
    if (!formData.isHome && (!formData.price || parseFloat(formData.price) <= 0)) {
      alert('Veuillez définir un prix pour le match à l\'extérieur');
      return;
    }

    if (!formData.isHome && formData.hasMinorPrice && (!formData.minorPrice || parseFloat(formData.minorPrice) <= 0)) {
      alert('Veuillez définir un prix mineur valide');
      return;
    }

    if (supportsSubscriberEmails && (!formData.responseDeadlineDate || !formData.responseDeadlineTime)) {
      alert('Veuillez saisir le délai de réponse des abonnés');
      return;
    }

    try {
      setSubmitting(true);
      
      const matchDateTime = parseDateTimeInTimeZone(formData.matchDate, formData.matchTime);
      
      const salesPhases = (formData.salesPhases || []).map(phase => ({
        title: phase.title,
        titleIt: phase.titleIt || '',
        startDate: parseDateTimeInTimeZone(phase.startDate, phase.startTime),
        endDate: parseDateTimeInTimeZone(phase.endDate, phase.endTime)
      }));

      const matchTitle = formData.isHome 
        ? `Juventus vs ${opponentName}`
        : `${opponentName} vs Juventus`;
      
      const competitionLogo = competitions[formData.competition]?.logo || '';

      const submitData = {
        matchTitle: matchTitle,
        competition: formData.competition,
        isHome: formData.isHome,
        opponentTeam: opponentName,
        competitionLogo: competitionLogo,
        date: matchDateTime,
        stadium: stadiumName,
        status: formData.status,
        description: formData.description || '',
        imageUrl: formData.imageUrl || '',
        active: formData.active !== undefined ? formData.active : true,
        salesPhases: salesPhases
      };

      // For home matches, use areaPricing
      if (formData.isHome) {
        // Group sectors by zone for areaPricing
        const zoneMap = {};
        pricingGroups.forEach(group => {
          group.sectors.forEach(sector => {
            const zone = getZoneFromSector(sector);
            if (!zone) return;
            if (!zoneMap[zone]) {
              zoneMap[zone] = [];
            }
            const sectorEntry = { name: sector, price: group.price };
            if (group.hasMinorPrice && group.minorPrice != null) {
              sectorEntry.minorPrice = group.minorPrice;
            }
            zoneMap[zone].push(sectorEntry);
          });
        });

        const areaPricing = Object.entries(zoneMap)
          .filter(([zone]) => zone)
          .map(([zone, sectors]) => ({
            area: zone,
            hasSectors: true,
            sectors: sectors
          }));
        
        submitData.areaPricing = areaPricing;
      } else {
        // For away matches, use single price
        submitData.price = parseFloat(formData.price);
        submitData.hasMinorPrice = Boolean(formData.hasMinorPrice);
        submitData.minorPrice = formData.hasMinorPrice
          ? parseFloat(formData.minorPrice)
          : null;
      }

      if (supportsSubscriberEmails && formData.responseDeadlineDate) {
        const deadlineDate = parseDateTimeInTimeZone(
          formData.responseDeadlineDate,
          formData.responseDeadlineTime,
        );
        if (!deadlineDate || Number.isNaN(deadlineDate.getTime())) {
          alert('Date ou heure limite de réponse des abonnés invalide');
          return;
        }
        submitData.subscriberResponseDeadline = deadlineDate.toISOString();
      }

      if (isHomeSerieA && formData.serieAHomeMatchIndex) {
        submitData.serieAHomeMatchIndex = parseInt(formData.serieAHomeMatchIndex, 10);
      } else {
        submitData.serieAHomeMatchIndex = null;
      }

      if (isEuropeanHome && formData.europeanHomeMatchIndex) {
        const europeanIndex = parseInt(formData.europeanHomeMatchIndex, 10);
        submitData.europeanHomeMatchIndex = europeanIndex;
        submitData.competitionPhase = europeanIndex >= 5 ? 'after_group_stage' : 'group_stage';
      } else {
        submitData.europeanHomeMatchIndex = null;
        submitData.competitionPhase = null;
      }

      if (isCoppaHome && formData.coppaMatchNumber) {
        submitData.coppaMatchNumber = parseInt(formData.coppaMatchNumber, 10);
      } else {
        submitData.coppaMatchNumber = null;
      }

      console.log('Submitting ticket data:', JSON.stringify(submitData, null, 2));

      if (isEditing) {
        await api.put(`/tickets/${id}`, submitData);
        alert('Billet mis à jour avec succès');
        navigate('/admin/tickets');
      } else {
        const response = await api.post('/tickets', submitData);
        alert('Billet créé avec succès');
        if (supportsSubscriberEmails) {
          navigate(`/admin/tickets/${response.data.ticket._id}/subscriber-notify`);
        } else {
          navigate('/admin/tickets');
        }
      }
    } catch (error) {
      console.error('Error saving ticket:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;
      alert('Erreur lors de la sauvegarde: ' + errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-container">
          <div className="loading">Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="admin-page-header">
          <button 
            className="btn-back"
            onClick={() => navigate('/admin/tickets')}
            style={{ 
              background: 'transparent', 
              border: 'none', 
              color: 'var(--juve-black)', 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '1rem',
              marginBottom: '1rem'
            }}
          >
            <FaArrowLeft /> Retour
          </button>
          <h1>{isEditing ? 'Modifier le billet' : 'Ajouter un billet'}</h1>
        </div>

        <form onSubmit={handleSubmit} className="admin-form">
          <div
            className="form-group"
            style={{
              padding: '1rem 1.25rem',
              background: '#f0f7ff',
              border: '1px solid #3498db',
              borderRadius: '8px',
              marginBottom: '1.5rem',
            }}
          >
            <label style={{ display: 'block', fontWeight: 700, marginBottom: '0.35rem' }}>
              Importer depuis un PDF Juventus (italien)
            </label>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', color: '#555', lineHeight: 1.45 }}>
              Chargez le document officiel du match (infos, <em>FASI DI VENDITA</em>,{' '}
              <em>Tabella prezzi</em>). Le formulaire sera prérempli automatiquement ; les
              libellés des phases sont traduits en français et l&apos;italien original est conservé.
            </p>
            <input
              ref={pdfInputRef}
              type="file"
              accept="application/pdf,.pdf"
              onChange={handlePdfImport}
              disabled={pdfImporting}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              disabled={pdfImporting}
              onClick={() => pdfInputRef.current?.click()}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.6rem 1rem',
                background: pdfImporting ? '#95a5a6' : '#2980b9',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: pdfImporting ? 'not-allowed' : 'pointer',
                fontWeight: 600,
              }}
            >
              <FaFilePdf />
              {pdfImporting ? 'Analyse du PDF…' : 'Choisir un PDF et remplir le formulaire'}
            </button>
            {pdfImportFeedback && (
              <p
                style={{
                  margin: '0.75rem 0 0',
                  fontSize: '0.9rem',
                  whiteSpace: 'pre-wrap',
                  color:
                    pdfImportFeedback.type === 'error'
                      ? '#c0392b'
                      : pdfImportFeedback.type === 'warning'
                        ? '#d35400'
                        : '#27ae60',
                }}
              >
                {pdfImportFeedback.message}
              </p>
            )}
          </div>

          <div className="form-group">
            <label>Compétition *</label>
            <select
              name="competition"
              value={formData.competition}
              onChange={handleChange}
              required
            >
              <option value="">Sélectionner une compétition</option>
              {Object.keys(competitions).map(comp => (
                <option key={comp} value={comp}>{comp}</option>
              ))}
            </select>
          </div>

          {formData.competition && (
            <>
              <div className="form-group">
                <label>Juventus joue *</label>
                <select
                  name="isHome"
                  value={formData.isHome ? 'home' : 'away'}
                  onChange={(e) => setFormData({ ...formData, isHome: e.target.value === 'home' })}
                  required
                >
                  <option value="home">À domicile (Home)</option>
                  <option value="away">À l'extérieur (Away)</option>
                </select>
              </div>

          {isEuropeanHome && (
            <div className="form-group">
              <label>Numéro du match à domicile (coupe européenne) *</label>
              <input
                type="number"
                name="europeanHomeMatchIndex"
                min="1"
                value={formData.europeanHomeMatchIndex}
                onChange={handleChange}
                required
                placeholder="1 = 1er match domicile, 5 = 5ème match domicile…"
              />
              <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.35rem' }}>
                Les abonnés STAR commandent un billet pour eux-mêmes à partir du 5ème match à domicile
                (Europa League, Conference League, Champions League). Matchs 1 à 4 : place couverte par l&apos;abonnement.
              </p>
            </div>
          )}

          {isHomeSerieA && (
            <div className="form-group">
              <label>Numéro du match à domicile (Serie A)</label>
              <input
                type="number"
                name="serieAHomeMatchIndex"
                min="1"
                max="19"
                value={formData.serieAHomeMatchIndex}
                onChange={handleChange}
                placeholder="1 à 19 (optionnel, pour suivi admin)"
              />
              <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.35rem' }}>
                Tous les matchs de championnat à domicile sont couverts par l&apos;abonnement (pas de billet titulaire).
              </p>
            </div>
          )}

          {isCoppaHome && (
            <div className="form-group">
              <label>Numéro du match à domicile (Coppa Italia) *</label>
              <input
                type="number"
                name="coppaMatchNumber"
                min="1"
                value={formData.coppaMatchNumber}
                onChange={handleChange}
                required
                placeholder="1 = 1er match domicile, 2 = 2ème match domicile…"
              />
              <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.35rem' }}>
                Les abonnés STAR commandent un billet pour eux-mêmes à partir du 2ème match à domicile.
              </p>
            </div>
          )}

          {isCoppaItalia && !formData.isHome && (
            <p style={{ fontSize: '0.85rem', color: '#666', margin: '0 0 1rem' }}>
              Match Coppa Italia à l&apos;extérieur : les abonnés commandent un billet pour eux-mêmes.
            </p>
          )}

              <div className="form-group">
                <label>Équipe adverse *</label>
                <select
                  name="opponentTeamSelect"
                  value={opponentSelectValue}
                  onChange={handleOpponentSelectChange}
                  required={!showCustomOpponentInput}
                  disabled={!formData.competition}
                >
                  <option value="">Sélectionner l'équipe adverse</option>
                  {competitionTeams.map((team) => (
                    <option key={team} value={team}>{team}</option>
                  ))}
                  <option value={CUSTOM_OPPONENT_VALUE}>Autre équipe (saisie libre)</option>
                </select>
                {showCustomOpponentInput && (
                  <div style={{ marginTop: '0.75rem' }}>
                    <label htmlFor="customOpponentTeam" style={{ display: 'block', marginBottom: '0.35rem' }}>
                      Nom de l&apos;équipe adverse *
                    </label>
                    <input
                      id="customOpponentTeam"
                      type="text"
                      name="customOpponentTeam"
                      value={customOpponentTeam}
                      onChange={handleCustomOpponentChange}
                      placeholder="Saisir le nom de l'équipe"
                      required
                      autoFocus
                    />
                  </div>
                )}
              </div>

              {resolvedOpponentName && !opponentHasLogo && (
                <div
                  className="form-group"
                  style={{
                    padding: '1rem 1.25rem',
                    background: '#fff8e6',
                    border: '1px solid #f0c040',
                    borderRadius: '8px',
                  }}
                >
                  <label style={{ fontWeight: 700, marginBottom: '0.5rem', display: 'block' }}>
                    Logo de l&apos;équipe adverse
                  </label>
                  <p style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', color: '#666' }}>
                    Aucun logo n&apos;est enregistré pour « {resolvedOpponentName} ». Vous pouvez en
                    ajouter un (JPG, PNG, WebP ou SVG — max. 2 Mo). Il sera réutilisé pour les
                    prochains billets de cette équipe.
                  </p>
                  <input
                    ref={opponentLogoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml,.svg"
                    onChange={handleOpponentLogoUpload}
                    disabled={opponentLogoUploading}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    className="btn-edit"
                    disabled={opponentLogoUploading}
                    onClick={() => opponentLogoInputRef.current?.click()}
                  >
                    {opponentLogoUploading ? 'Envoi en cours…' : 'Ajouter un logo'}
                  </button>
                </div>
              )}

              {resolvedOpponentName && opponentHasLogo && (
                <div className="form-group" style={{ marginTop: '-0.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.9rem' }}>
                    Logo de l&apos;équipe adverse
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    <img
                      key={`${resolvedOpponentName}-${teamLogosTick}`}
                      src={opponentLogoSrc}
                      alt={resolvedOpponentName}
                      style={{ height: 48, maxWidth: 120, objectFit: 'contain' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <input
                      ref={opponentLogoInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml,.svg"
                      onChange={handleOpponentLogoUpload}
                      disabled={opponentLogoUploading}
                      style={{ display: 'none' }}
                    />
                    <button
                      type="button"
                      className="btn-edit"
                      disabled={opponentLogoUploading}
                      onClick={() => opponentLogoInputRef.current?.click()}
                    >
                      {opponentLogoUploading ? 'Envoi…' : 'Remplacer le logo'}
                    </button>
                  </div>
                </div>
              )}

              <div className="form-group" style={{ 
                padding: '1rem', 
                background: '#f8f9fa', 
                borderRadius: '6px',
                border: '1px solid #e0e0e0'
              }}>
                <label style={{ marginBottom: '0.5rem', fontWeight: 600 }}>Aperçu du match:</label>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '1rem',
                  flexWrap: 'wrap'
                }}>
                  {formData.isHome ? (
                    <>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.35rem' }}>
                        <img
                          src={juventusLogoSrc}
                          alt="Juventus"
                          style={{ height: 40, maxWidth: 40, objectFit: 'contain' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <span style={{ fontWeight: 700 }}>Juventus</span>
                      </div>
                      <span>vs</span>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.35rem' }}>
                        {resolvedOpponentName && opponentHasLogo && (
                          <img
                            key={`preview-opp-${teamLogosTick}`}
                            src={opponentLogoSrc}
                            alt={resolvedOpponentName}
                            style={{ height: 40, maxWidth: 40, objectFit: 'contain' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <span>{resolvedOpponentName || 'Équipe adverse'}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.35rem' }}>
                        {resolvedOpponentName && opponentHasLogo && (
                          <img
                            key={`preview-opp-${teamLogosTick}`}
                            src={opponentLogoSrc}
                            alt={resolvedOpponentName}
                            style={{ height: 40, maxWidth: 40, objectFit: 'contain' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <span>{resolvedOpponentName || 'Équipe adverse'}</span>
                      </div>
                      <span>vs</span>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.35rem' }}>
                        <img
                          src={juventusLogoSrc}
                          alt="Juventus"
                          style={{ height: 40, maxWidth: 40, objectFit: 'contain' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <span style={{ fontWeight: 700 }}>Juventus</span>
                      </div>
                    </>
                  )}
                  {formData.competition && competitions[formData.competition]?.logo && (
                    <img 
                      src={competitions[formData.competition].logo} 
                      alt={formData.competition}
                      style={{ 
                        height: '30px', 
                        marginLeft: 'auto',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                </div>
              </div>
            </>
          )}

          <div className="form-group">
            <label>Stade *</label>
            <select
              name="stadium"
              value={stadiumSelectValue}
              onChange={handleStadiumSelectChange}
              required={!showCustomStadiumInput}
            >
              {stadiums.map(stadium => (
                <option key={stadium} value={stadium}>{stadium}</option>
              ))}
              <option value={CUSTOM_STADIUM_VALUE}>Autre stade (saisie libre)</option>
            </select>
            {showCustomStadiumInput && (
              <input
                type="text"
                name="customStadium"
                value={customStadium}
                onChange={handleCustomStadiumChange}
                placeholder="Nom du stade"
                required
                style={{ marginTop: '0.5rem' }}
              />
            )}
          </div>

          <div className="form-group">
            <label>Date du match *</label>
            <input
              type="date"
              name="matchDate"
              value={formData.matchDate}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Heure du match * (heure d&apos;Italie)</label>
            <input
              type="time"
              name="matchTime"
              value={formData.matchTime}
              onChange={handleChange}
              required
            />
          </div>

          {supportsSubscriberEmails && (
            <div
              className="form-group"
              style={{
                padding: '1rem 1.25rem',
                background: '#fff8e6',
                border: '1px solid #f0c040',
                borderRadius: '8px',
              }}
            >
              <label style={{ fontWeight: 700, marginBottom: '0.5rem', display: 'block' }}>
                Délai de réponse des abonnés *
              </label>
              <p style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', color: '#666' }}>
                Les abonnés BASE, FULL, STAR et STAR abo club recevront un e-mail leur demandant de confirmer
                leur participation. Passé ce délai, ils ne pourront plus répondre.
              </p>
              {isEditing && subscriberEmailsSent && (
                <p style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', color: '#555' }}>
                  Les e-mails ont déjà été envoyés : en modifiant cette date, le délai sera aussi mis à jour
                  pour toutes les réponses abonnés enregistrées.
                </p>
              )}
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.9rem' }}>
                    Date limite
                  </label>
                  <input
                    type="date"
                    name="responseDeadlineDate"
                    value={formData.responseDeadlineDate}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.9rem' }}>
                    Heure limite
                  </label>
                  <input
                    type="time"
                    name="responseDeadlineTime"
                    value={formData.responseDeadlineTime}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>
          )}

          <div className="form-group">
            <label>Disponibilité</label>
            <label className="toggle-switch" style={{ display: 'inline-block', marginTop: '0.5rem' }}>
              <input
                type="checkbox"
                checked={formData.status === 'available'}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    status: e.target.checked ? 'available' : 'full'
                  });
                }}
              />
              <span className="toggle-slider">
                {formData.status === 'available' ? 'Disponible' : 'Complet'}
              </span>
            </label>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
            />
          </div>

          <div className="form-group checkbox">
            <label>
              <input
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleChange}
              />
              Actif
            </label>
          </div>

          {/* Stadium Map for Sector Price Setting - ONLY FOR HOME MATCHES */}
          {/* For home matches (isHome = true): Show Allianz Stadium map with sector selection */}
          {/* For away matches (isHome = false): Show only single price input (see else block below) */}
          {formData.isHome ? (
            <div className="form-group" style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '2px solid #e0e0e0' }}>
              <label style={{ marginBottom: '1rem', fontSize: '1.1rem', fontWeight: 700 }}>
                Définir les prix par secteur
              </label>
              <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1.5rem' }}>
                Cliquez sur les secteurs dans le plan du stade Allianz pour les sélectionner. Sélectionnez plusieurs secteurs, définissez un prix unique pour tous, puis sauvegardez. Les secteurs déjà tarifés apparaissent en doré et ne peuvent plus être sélectionnés.
                Après un import PDF, vous pouvez ajouter un prix mineur pour chaque groupe dans la liste ci-dessous.
              </p>
            
            {/* Price input for current selection */}
            {tempSelectedSectors.length > 0 && (
              <div style={{ 
                marginBottom: '1.5rem', 
                padding: '1rem', 
                background: '#e3f2fd', 
                borderRadius: '8px',
                border: '1px solid #90caf9',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                flexWrap: 'wrap'
              }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Prix pour {tempSelectedSectors.length} secteur(s) sélectionné(s) (€) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={currentPrice}
                    onChange={(e) => setCurrentPrice(e.target.value)}
                    placeholder="0.00"
                    style={{
                      width: '150px',
                      padding: '0.5rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '1rem'
                    }}
                  />
                  <div style={{ marginTop: '0.75rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={currentHasMinorPrice}
                        onChange={(e) => {
                          setCurrentHasMinorPrice(e.target.checked);
                          if (!e.target.checked) setCurrentMinorPrice('');
                        }}
                      />
                      Prix mineur disponible
                    </label>
                    {currentHasMinorPrice && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.9rem' }}>
                          Prix mineur (€) *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={currentMinorPrice}
                          onChange={(e) => setCurrentMinorPrice(e.target.value)}
                          placeholder="0.00"
                          style={{
                            width: '150px',
                            padding: '0.5rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '1rem'
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={savePricingGroup}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#27ae60',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <FaPlus /> Enregistrer le prix
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setTempSelectedSectors([]);
                    setCurrentPrice('');
                    setCurrentHasMinorPrice(false);
                    setCurrentMinorPrice('');
                  }}
                  style={{
                    padding: '0.75rem 1rem',
                    background: '#95a5a6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  Annuler
                </button>
              </div>
            )}
            
            <div className="stadium-map-container">
              <div className={isMobile ? 'stadium-map-wrapper-mobile' : 'stadium-map-wrapper'}>
                <img
                  ref={stadiumImageRef}
                  src={stadiumPlan}
                  alt="Plan du stade Juventus"
                  className={isMobile ? 'stadium-plan-image-mobile' : 'stadium-plan-image'}
                  useMap="#stadium-map-admin"
                  onLoad={(e) => {
                    setImageLoaded(true);
                    const img = e.target;
                    setImageDimensions({
                      width: img.naturalWidth || 775,
                      height: img.naturalHeight || 615
                    });
                    // Use actual displayed dimensions for the SVG overlay
                    const displayWidth = img.offsetWidth || img.naturalWidth || 775;
                    const displayHeight = img.offsetHeight || img.naturalHeight || 615;
                    setDisplayDimensions({
                      width: displayWidth,
                      height: displayHeight
                    });
                  }}
                />
                {/* SVG Overlay for hover and selection effects */}
                {imageLoaded && stadiumImageRef.current && (() => {
                  const img = stadiumImageRef.current;
                  const imgWidth = img.offsetWidth || img.naturalWidth || 775;
                  const imgHeight = img.offsetHeight || img.naturalHeight || 615;
                  
                  return (
                    <svg
                      key={`svg-${imgWidth}-${imgHeight}`}
                      className="stadium-overlay"
                      style={{
                        width: `${imgWidth}px`,
                        height: `${imgHeight}px`,
                        position: 'absolute',
                        top: '0',
                        left: '0'
                      }}
                      viewBox={`0 0 ${imageDimensions.width} ${imageDimensions.height}`}
                      preserveAspectRatio="none"
                    >
                      {/* Hover effect */}
                      {hoveredAreaCoords && (
                        <g>
                          {hoveredAreaCoords.type === 'rect' && (
                            <rect
                              x={hoveredAreaCoords.x}
                              y={hoveredAreaCoords.y}
                              width={hoveredAreaCoords.width}
                              height={hoveredAreaCoords.height}
                              fill="rgba(0, 0, 0, 0.4)"
                              className="sector-hover-overlay"
                            />
                          )}
                          {hoveredAreaCoords.type === 'poly' && (
                            <polygon
                              points={hoveredAreaCoords.points.map(p => `${p.x},${p.y}`).join(' ')}
                              fill="rgba(0, 0, 0, 0.4)"
                              className="sector-hover-overlay"
                            />
                          )}
                        </g>
                      )}
                      {/* Temporarily selected sectors (being selected) */}
                      {tempSelectedSectors.map((sector) => {
                        const mapElement = document.getElementById('stadium-map-admin');
                        if (!mapElement) return null;
                        const selectedArea = Array.from(mapElement.querySelectorAll('area')).find(
                          a => a.getAttribute('title') === sector
                        );
                        if (!selectedArea) return null;
                        const coords = selectedArea.getAttribute('coords');
                        const shape = selectedArea.getAttribute('shape');
                        const areaInfo = parseAreaCoords(coords, shape);
                        if (!areaInfo) return null;
                        
                        if (areaInfo.type === 'rect') {
                          return (
                            <rect
                              key={`temp-${sector}`}
                              x={areaInfo.x}
                              y={areaInfo.y}
                              width={areaInfo.width}
                              height={areaInfo.height}
                              fill="rgba(52, 152, 219, 0.3)"
                              stroke="rgba(52, 152, 219, 0.8)"
                              strokeWidth="2"
                              className="sector-selected-overlay"
                            />
                          );
                        } else if (areaInfo.type === 'poly') {
                          return (
                            <polygon
                              key={`temp-${sector}`}
                              points={areaInfo.points.map(p => `${p.x},${p.y}`).join(' ')}
                              fill="rgba(52, 152, 219, 0.3)"
                              stroke="rgba(52, 152, 219, 0.8)"
                              strokeWidth="2"
                              className="sector-selected-overlay"
                            />
                          );
                        }
                        return null;
                      })}
                      {/* Saved pricing groups (already priced sectors) */}
                      {pricingGroups.flatMap(group => group.sectors).map((sector) => {
                        const mapElement = document.getElementById('stadium-map-admin');
                        if (!mapElement) return null;
                        const selectedArea = Array.from(mapElement.querySelectorAll('area')).find(
                          a => a.getAttribute('title') === sector
                        );
                        if (!selectedArea) return null;
                        const coords = selectedArea.getAttribute('coords');
                        const shape = selectedArea.getAttribute('shape');
                        const areaInfo = parseAreaCoords(coords, shape);
                        if (!areaInfo) return null;
                        
                        if (areaInfo.type === 'rect') {
                          return (
                            <rect
                              key={`priced-${sector}`}
                              x={areaInfo.x}
                              y={areaInfo.y}
                              width={areaInfo.width}
                              height={areaInfo.height}
                              fill="rgba(184, 155, 95, 0.3)"
                              stroke="rgba(184, 155, 95, 0.8)"
                              strokeWidth="2"
                              className="sector-selected-overlay"
                            />
                          );
                        } else if (areaInfo.type === 'poly') {
                          return (
                            <polygon
                              key={`priced-${sector}`}
                              points={areaInfo.points.map(p => `${p.x},${p.y}`).join(' ')}
                              fill="rgba(184, 155, 95, 0.3)"
                              stroke="rgba(184, 155, 95, 0.8)"
                              strokeWidth="2"
                              className="sector-selected-overlay"
                            />
                          );
                        }
                        return null;
                      })}
                    </svg>
                  );
                })()}
                <map name="stadium-map-admin" id="stadium-map-admin">
                  <area target="" alt="111" title="111" href="#" coords="241,95,156,121,210,175,236,167" shape="poly" />
                  <area target="" alt="117" title="117" href="#" coords="549,95,624,126,571,176,545,168" shape="poly" />
                  <area target="" alt="119" title="119" href="#" coords="579,207,648,269" shape="rect" />
                  <area target="" alt="120" title="120" href="#" coords="578,273,647,334" shape="rect" />
                  <area target="" alt="121" title="121" href="#" coords="580,339,648,393" shape="rect" />
                  <area target="" alt="109" title="109" href="#" coords="124,203,199,264" shape="rect" />
                  <area target="" alt="108" title="108" href="#" coords="124,269,198,325" shape="rect" />
                  <area target="" alt="107" title="107" href="#" coords="125,330,197,388" shape="rect" />
                  <area target="" alt="201" title="201" href="#" coords="355,551,403,515" shape="rect" />
                  <area target="" alt="232" title="232" href="#" coords="475,547,412,516" shape="rect" />
                  <area target="" alt="231" title="231" href="#" coords="482,510,544,549" shape="rect" />
                  <area target="" alt="202" title="202" href="#" coords="343,515,294,545" shape="rect" />
                  <area target="" alt="203" title="203" href="#" coords="288,516,236,551" shape="rect" />
                  <area target="" alt="204" title="204" href="#" coords="233,508,182,552" shape="rect" />
                  <area target="" alt="124" title="124" href="#" coords="528,436,504,464" shape="rect" />
                  <area target="" alt="125" title="125" href="#" coords="493,434,470,456" shape="rect" />
                  <area target="" alt="102" title="102" href="#" coords="343,435,307,467" shape="rect" />
                  <area target="" alt="113" title="113" href="#" coords="301,92,357,169" shape="rect" />
                  <area target="" alt="114" title="114" href="#" coords="419,170,362,91" shape="rect" />
                  <area target="" alt="115" title="115" href="#" coords="425,92,478,169" shape="rect" />
                  <area target="" alt="116" title="116" href="#" coords="486,94,542,167" shape="rect" />
                  <area target="" alt="112" title="112" href="#" coords="243,95,299,167" shape="rect" />
                  <area target="" alt="210" title="210" href="#" coords="81,205,119,262" shape="rect" />
                  <area target="" alt="209" title="209" href="#" coords="78,322,119,268" shape="rect" />
                  <area target="" alt="208" title="208" href="#" coords="119,329,75,393" shape="rect" />
                  <area target="" alt="224" title="224" href="#" coords="695,206,657,269" shape="rect" />
                  <area target="" alt="225" title="225" href="#" coords="695,278,657,331" shape="rect" />
                  <area target="" alt="226" title="226" href="#" coords="695,342,658,396" shape="rect" />
                  <area target="" alt="118" title="118" href="#" coords="629,129,650,200,582,200,576,178" shape="poly" />
                  <area target="" alt="110" title="110" href="#" coords="155,128,201,175,196,195,126,196" shape="poly" />
                  <area target="" alt="106" title="106" href="#" coords="197,397,123,396,153,477,207,425" shape="poly" />
                  <area target="" alt="211" title="211" href="#" coords="90,142,84,198,119,200,125,161" shape="poly" />
                  <area target="" alt="212" title="212" href="#" coords="96,132,122,95,145,121,129,152" shape="poly" />
                  <area target="" alt="213" title="213" href="#" coords="128,86,170,61,184,98,147,117" shape="poly" />
                  <area target="" alt="214" title="214" href="#" coords="176,58,234,50,238,82,187,95" shape="poly" />
                  <area target="" alt="207" title="207" href="#" coords="119,400,76,402,85,457,126,446" shape="poly" />
                  <area target="" alt="206" title="206" href="#" coords="92,466,118,507,145,482,129,451" shape="poly" />
                  <area target="" alt="205" title="205" href="#" coords="121,509,171,543,175,499,153,484" shape="poly" />
                  <area target="" alt="215" title="215" href="#" coords="241,52,285,52,292,89,249,87" shape="poly" />
                  <area target="" alt="216" title="216" href="#" coords="297,46,357,88" shape="rect" />
                  <area target="" alt="217" title="217" href="#" coords="368,49,419,86" shape="rect" />
                  <area target="" alt="218" title="218" href="#" coords="424,48,478,85" shape="rect" />
                  <area target="" alt="219" title="219" href="#" coords="491,47,538,88" shape="rect" />
                  <area target="" alt="220" title="220" href="#" coords="546,45,594,93" shape="rect" />
                  <area target="" alt="221" title="221" href="#" coords="606,65,647,89,618,120,595,105" shape="poly" />
                  <area target="" alt="222" title="222" href="#" coords="655,96,681,136,650,154,631,120" shape="poly" />
                  <area target="" alt="223" title="223" href="#" coords="679,144,692,196,655,195,652,163" shape="poly" />
                  <area target="" alt="227" title="227" href="#" coords="655,401,650,440,687,454,697,405" shape="poly" />
                  <area target="" alt="228" title="228" href="#" coords="650,450,682,465,656,505,626,478" shape="poly" />
                  <area target="" alt="229" title="229" href="#" coords="649,507,609,535,593,506,622,482" shape="poly" />
                  <area target="" alt="230" title="230" href="#" coords="586,507,601,540,546,547,550,513" shape="poly" />
                  <area target="" alt="Legends club" title="Legends club" href="#" coords="193,470,592,501" shape="rect" />
                  <area target="" alt="103" title="103" href="#" coords="302,428,277,463" shape="rect" />
                  <area target="" alt="104" title="104" href="#" coords="274,430,248,464" shape="rect" />
                  <area target="" alt="105" title="105" href="#" coords="245,429,206,462" shape="rect" />
                  <area target="" alt="126" title="126" href="#" coords="465,436,430,464" shape="rect" />
                  <area target="" alt="123" title="123" href="#" coords="568,429,538,464" shape="rect" />
                  <area target="" alt="101" title="101" href="#" coords="347,437,422,463" shape="rect" />
                  <area target="" alt="122" title="122" href="#" coords="579,399,643,402,623,469,574,428" shape="poly" />
                </map>
              </div>
              
              {/* Saved Pricing Groups */}
              {pricingGroups.length > 0 && (
                <div style={{ 
                  marginTop: '2rem', 
                  background: '#f8f9fa', 
                  padding: '1.5rem', 
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0'
                }}>
                  <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1.2rem' }}>
                    Groupes de prix définis ({pricingGroups.length})
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {pricingGroups.map((group) => (
                      <div 
                        key={group.id}
                        style={{
                          padding: '1rem',
                          background: 'white',
                          borderRadius: '6px',
                          border: '1px solid #e0e0e0'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                          <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>
                            Prix: {group.price.toFixed(2)} €
                            {group.hasMinorPrice && group.minorPrice != null && (
                              <span style={{ marginLeft: '1rem', fontWeight: 500, color: '#555' }}>
                                Mineur: {group.minorPrice.toFixed(2)} €
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => removePricingGroup(group.id)}
                            style={{
                              padding: '0.5rem',
                              background: '#e74c3c',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title="Supprimer ce groupe"
                          >
                            <FaTimes />
                          </button>
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.75rem' }}>
                          Secteurs ({group.sectors.length}): {group.sectors.join(', ')}
                        </div>
                        <div
                          style={{
                            paddingTop: '0.75rem',
                            borderTop: '1px solid #eee',
                          }}
                        >
                          <label
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              cursor: 'pointer',
                              fontSize: '0.9rem',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={Boolean(group.hasMinorPrice)}
                              onChange={(e) =>
                                updatePricingGroupMinorPrice(
                                  group.id,
                                  e.target.checked,
                                  e.target.checked ? group.minorPrice ?? '' : '',
                                )
                              }
                            />
                            Prix mineur disponible
                          </label>
                          {group.hasMinorPrice && (
                            <div style={{ marginTop: '0.5rem' }}>
                              <label
                                style={{
                                  display: 'block',
                                  marginBottom: '0.35rem',
                                  fontSize: '0.9rem',
                                  fontWeight: 600,
                                }}
                              >
                                Prix mineur (€)
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={group.minorPrice ?? ''}
                                onChange={(e) =>
                                  updatePricingGroupMinorPrice(group.id, true, e.target.value)
                                }
                                placeholder="0.00"
                                style={{
                                  width: '150px',
                                  padding: '0.5rem',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  fontSize: '1rem',
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {pricingGroups.length === 0 && tempSelectedSectors.length === 0 && (
                <div className="sector-instruction" style={{ marginTop: '1rem' }}>
                  <p>Aucun secteur tarifé. Cliquez sur les secteurs dans le plan du stade pour commencer.</p>
                </div>
              )}
            </div>
          </div>
          ) : (
            /* AWAY MATCHES: Single price input only (no stadium map) */
            <div className="form-group" style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '2px solid #e0e0e0' }}>
              <label style={{ marginBottom: '1rem', fontSize: '1.1rem', fontWeight: 700 }}>
                Prix du billet
              </label>
              <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1.5rem' }}>
                Pour les matchs à l'extérieur, définissez un prix unique pour tous les billets. Le plan du stade n'est pas nécessaire car les billets sont achetés directement auprès du club adverse.
              </p>
              <div style={{ 
                padding: '1rem', 
                background: '#f8f9fa', 
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
                maxWidth: '400px'
              }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Prix adulte (€) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  placeholder="0.00"
                  required={!formData.isHome}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem'
                  }}
                />
                <div style={{ marginTop: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      name="hasMinorPrice"
                      checked={formData.hasMinorPrice}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          hasMinorPrice: e.target.checked,
                          minorPrice: e.target.checked ? formData.minorPrice : '',
                        });
                      }}
                    />
                    Prix mineur disponible
                  </label>
                  {formData.hasMinorPrice && (
                    <div style={{ marginTop: '0.75rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                        Prix mineur (€) *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        name="minorPrice"
                        value={formData.minorPrice}
                        onChange={handleChange}
                        placeholder="0.00"
                        required={formData.hasMinorPrice}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '1rem'
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Sales Phases Section */}
          <div className="form-group" style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '2px solid #e0e0e0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <label style={{ marginBottom: 0, fontSize: '1.1rem', fontWeight: 700 }}>Phases de vente</label>
              {formData.salesPhases.length < MAX_SALES_PHASES && (
                <button
                  type="button"
                  onClick={addSalesPhase}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#3498db',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <FaPlus /> Ajouter une phase
                </button>
              )}
            </div>
            <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
              Vous pouvez ajouter jusqu'à {MAX_SALES_PHASES} phases de vente. Chaque phase définit une période pendant laquelle les billets sont disponibles.
            </p>

            {formData.salesPhases.length === 0 && (
              <div style={{ 
                padding: '1.5rem', 
                background: '#f8f9fa', 
                borderRadius: '6px', 
                textAlign: 'center',
                color: '#666'
              }}>
                Aucune phase de vente définie. Cliquez sur "Ajouter une phase" pour en créer une.
              </div>
            )}

            {formData.salesPhases.map((phase, index) => (
              <div 
                key={index}
                style={{
                  background: '#f8f9fa',
                  padding: '1.5rem',
                  borderRadius: '6px',
                  marginBottom: '1rem',
                  border: '1px solid #e0e0e0'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h4 style={{ margin: 0, color: 'var(--juve-black)' }}>Phase {index + 1}</h4>
                  <button
                    type="button"
                    onClick={() => removeSalesPhase(index)}
                    style={{
                      padding: '0.4rem 0.8rem',
                      background: '#e74c3c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.3rem',
                      fontSize: '0.85rem'
                    }}
                  >
                    <FaTimes /> Supprimer
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Titre de la phase (français) *</label>
                    <input
                      type="text"
                      value={phase.title}
                      onChange={(e) => handleSalesPhaseChange(index, 'title', e.target.value)}
                      placeholder="Ex: Vente anticipée, Vente générale, etc."
                      required
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Titre de la phase (italiano)</label>
                    <input
                      type="text"
                      value={phase.titleIt || ''}
                      onChange={(e) => handleSalesPhaseChange(index, 'titleIt', e.target.value)}
                      placeholder="Es: Prevendita, Vendita generale, etc."
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div className="form-group">
                    <label>Date de début *</label>
                    <input
                      type="date"
                      value={phase.startDate}
                      onChange={(e) => handleSalesPhaseChange(index, 'startDate', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Heure de début *</label>
                    <input
                      type="time"
                      value={phase.startTime}
                      onChange={(e) => handleSalesPhaseChange(index, 'startTime', e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label>Date de fin *</label>
                    <input
                      type="date"
                      value={phase.endDate}
                      onChange={(e) => handleSalesPhaseChange(index, 'endDate', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Heure de fin *</label>
                    <input
                      type="time"
                      value={phase.endTime}
                      onChange={(e) => handleSalesPhaseChange(index, 'endTime', e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-submit" disabled={submitting}>
              {submitting ? 'Enregistrement...' : (isEditing ? 'Mettre à jour' : 'Créer')}
            </button>
            <button 
              type="button" 
              className="btn-cancel"
              onClick={() => navigate('/admin/tickets')}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminTicketForm;
